import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import ATbutton from '@at.govt.nz/buttons';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { ReactComponent as IconPlus } from '../../assets/svg/icon-plus-circle.svg';
import { ReactComponent as IconTick } from '../../assets/svg/icon-tick-circle.svg';
import { getFilterRoutesOptionsByStop } from '../../services/LiveDepartureService';
import LiveDepartureActions from '../../redux/LiveDeparture';
import Confirm from '../../components/Confirm';
import { titleCase } from '../../utils/Humanizers';

class FilterRouteOptionsDialog extends PureComponent {
  static _createRoutesChecked(filteredRoutesNames, routesNameList) {
    if (_.isEmpty(filteredRoutesNames)) {
      return new Set(routesNameList);
    }
    return new Set(filteredRoutesNames);
  }

  static _createCheckboxAriaLabel(route, isChecked) {
    const action = isChecked ? 'to uncheck' : 'to check';
    return `check box, route ${route.route_short_name} going through from ${route.route_long_name}, click ${action}`;
  }

  static propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    stop: PropTypes.object.isRequired,
    filteredRoutesNames: PropTypes.array,
    storeFilteredRoutesNames: PropTypes.func.isRequired,
  };

  static defaultProps = {
    filteredRoutesNames: null,
    isOpen: false,
  };

  constructor(props) {
    super(props);
    this.state = {
      routesGrouped: {},
      routesNameListSorted: null,
      routesChecked: new Set(),
    };
    this._isMounted = true;
  }

  componentDidMount() {
    const { stop, filteredRoutesNames } = this.props;
    const stopCode = stop?.stopCode;

    if (!stopCode) {
      return;
    }

    if (!_.isNil(filteredRoutesNames)) {
      getFilterRoutesOptionsByStop(stopCode)
        .then((options) => {
          if (this._isMounted) {
            const { routesGrouped, routesNameListSorted } = options;
            const routesChecked = FilterRouteOptionsDialog._createRoutesChecked(filteredRoutesNames, routesNameListSorted);
            this.setState(() => ({
              routesChecked,
              routesGrouped,
              routesNameListSorted,
            }));
          }
        });
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  _createOnSelectAll = isSelectAll => () => {
    const { routesGrouped } = this.state;
    let routesChecked = new Set();
    const routeNames = Object.keys(routesGrouped);
    if (isSelectAll) {
      routesChecked = new Set(routeNames);
    }
    this.setState(() => ({
      routesChecked,
    }));
  };

  _onClose = () => {
    this._createOnConfirm(false)();
  };

  _createOnConfirm = shouldConfirm => () => {
    const { routesChecked, routesNameListSorted } = this.state;
    const {
      onClose, stop, filteredRoutesNames, storeFilteredRoutesNames,
    } = this.props;
    if (!shouldConfirm) {
      this.setState(() => ({
        routesChecked: FilterRouteOptionsDialog._createRoutesChecked(filteredRoutesNames, routesNameListSorted),
      }));
    } else {
      storeFilteredRoutesNames(Array.from(routesChecked), stop.stopCode, true);
    }
    onClose();
  };


  _createOnRouteCheck = routeShortName => () => {
    const { routesChecked } = this.state;

    const routesCheckedUpdated = new Set(routesChecked);
    if (routesCheckedUpdated.has(routeShortName)) {
      routesCheckedUpdated.delete(routeShortName);
    } else {
      routesCheckedUpdated.add(routeShortName);
    }

    this.setState(() => ({
      routesChecked: routesCheckedUpdated,
    }));
  };

  _renderFilterItem = (routes) => {
    if (_.isEmpty(routes)) {
      return null;
    }

    const shortName = routes[0].route_short_name;
    const { routesChecked } = this.state;
    const isChecked = routesChecked.has(shortName);
    const Icon = isChecked ? IconTick : IconPlus;

    return (
      <div
        className="filter-route-item filter-route-item-separator"
        key={`${shortName}`}
        role="checkbox"
        tabIndex="0"
        aria-checked={isChecked}
        onClick={this._createOnRouteCheck(shortName)}
        onKeyPress={this._createOnRouteCheck(shortName)}
        aria-label={FilterRouteOptionsDialog._createCheckboxAriaLabel(routes[0], isChecked)}
        data-cy={`checkbox-${shortName}`}
      >
        <div className="filter-route-check-box-container">
          <Icon className="filter-route-check-box-icon" role="img" />
        </div>
        <div className="filter-route-item-description">
          <p className="filter-route-item-route-name">
            {shortName}
          </p>
          {routes.map(route => (
            <p className="filter-route-item-direction" key={`${route.route_long_name}`}>
              {`To ${route.stop_headsigns.map(headsign => titleCase(headsign)).join(' / ')}`}
            </p>
          ))}
        </div>
      </div>
    );
  };

  _renderFilterList = () => {
    const { routesGrouped, routesNameListSorted } = this.state;
    if (!routesNameListSorted) {
      return (
        <div className="filter-route-list filter-route-loader-container">
          <div className="loader filter-route-loader-loader" />
        </div>
      );
    }
    return (
      <ul className="filter-route-list">
        {routesNameListSorted.map(name => this._renderFilterItem(routesGrouped[name]))}
      </ul>
    );
  };

  render() {
    const { routesChecked } = this.state;
    const { isOpen } = this.props;
    return (
      <Confirm className="filter-route-dialog" disabled={_.isEmpty(routesChecked)} isOpen={isOpen} title="Filter routes" onCancel={this._createOnConfirm(false)} onConfirm={this._createOnConfirm(true)}>
        <div className="filter-route-select-button-container">
          <ATbutton className="filter-route-select-button" onClick={this._createOnSelectAll(true)}>
            Select All
          </ATbutton>
          <div className="filter-route-select-button-separator" />
          <ATbutton className="filter-route-select-button" onClick={this._createOnSelectAll(false)}>
            De-select All
          </ATbutton>
        </div>
        {this._renderFilterList()}
      </Confirm>
    );
  }
}

const mapStateToProps = state => ({
  filteredRoutesNames: state.liveDeparture.filteredRoutesNames,
});

const mapDispatchToProps = dispatch => ({
  storeFilteredRoutesNames: (routesNames, stopCode) => dispatch(LiveDepartureActions.storeFilteredRoutesNames(routesNames, stopCode)),
});

export default connect(mapStateToProps, mapDispatchToProps)(FilterRouteOptionsDialog);
